import {
  ChatPromptTranslate,
  LanguageEnum,
  QueryReadChatPromptTranslateArgs,
  ReadChatPromptTranslateType
} from '@/generated/graphql.mia'
import { READ_CHAT_PROMPT_TRANSLATE } from '@/graphql/mia/queries/readChatPrompt'
import { useQuery } from '@vue/apollo-composable'
import { defineStore, storeToRefs } from 'pinia'
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCategoriesStore } from '@/stores/categoriesStore'
import { useConversationStore } from '@/stores/conversationStore'
import Clarity from '@microsoft/clarity'

declare const clarity: any

export const usePromptsStore = defineStore('promptsStore', () => {
  const { locale } = useI18n()
  const categoriesStore = useCategoriesStore()
  const { activeCategory } = storeToRefs(categoriesStore)

  const conversationStore = useConversationStore()
  const { handleSendMessage } = conversationStore
  const { promptMessage } = storeToRefs(conversationStore)

  const promptsOffset = ref(0)
  const promptsLimit = 15

  const { onError: onReadChatPromptTranslateError, refetch: readChatPromptTranslateRefetch } = useQuery<
    { readChatPromptTranslate: ReadChatPromptTranslateType },
    QueryReadChatPromptTranslateArgs
  >(
    READ_CHAT_PROMPT_TRANSLATE,
    {
      chatPromptTranslateInput: {
        categoryId: activeCategory.value,
        languageEnum: locale.value.toLocaleUpperCase() as LanguageEnum,
        blockSize: 0,
        offset: 0
      }
    },
    {
      clientId: 'mia'
    }
  )

  onReadChatPromptTranslateError(() => {
    console.error('Error while fetching prompts')
  })

  const prompts = ref<ChatPromptTranslate[]>([])

  const loadMorePrompts = async () => {
    const vars: QueryReadChatPromptTranslateArgs = {
      chatPromptTranslateInput: {
        categoryId: activeCategory.value,
        languageEnum: locale.value.toLocaleUpperCase() as LanguageEnum,
        blockSize: promptsLimit,
        offset: promptsOffset.value
      }
    }

    promptsOffset.value += promptsLimit
    const res = await readChatPromptTranslateRefetch(vars)

    const data = res?.data?.readChatPromptTranslate?.data

    if (typeof data === 'undefined' || data === null) {
      return
    }

    prompts.value.push(...data)
  }

  onMounted(() => {
    loadMorePrompts()
  })

  watch([activeCategory, locale], () => {
    promptsOffset.value = 0
    prompts.value = []
    loadMorePrompts()
  })

  const selectPrompt = (prompt: ChatPromptTranslate) => {
    Clarity.event('clickedDefaultPrompt')
    Clarity.setTag('promptId', String(prompt.id))

    promptMessage.value = prompt.ngChatPrompt!
    handleSendMessage()
    promptMessage.value = ''
  }

  return { prompts, loadMorePrompts, selectPrompt, activeCategory }
})
